/**
 * Sexy Hair Predictive Search
 * Simple search handler that fetches predictive results and injects them into the drawer.
 * Replaces Dawn's PredictiveSearch/SearchForm web components.
 *
 * Uses document-level event delegation so it survives Shopify theme editor
 * section reloads without re-binding. Per-input debounce + AbortController
 * prevents the mobile and desktop drawers from interfering with each other.
 *
 * Injected markup IDs are rewritten with a per-input prefix so mobile + desktop
 * drawers never produce duplicate IDs in the DOM.
 */
(function () {
  // Per-input state stored on the element itself to avoid cross-drawer interference
  var INPUT_KEY = '__sexyhairSearch';

  function getState(input) {
    if (!input[INPUT_KEY]) {
      input[INPUT_KEY] = { abortController: null, debounceTimer: null };
    }
    return input[INPUT_KEY];
  }

  /**
   * Rewrite fixed IDs in fetched markup to avoid duplicates when both
   * mobile + desktop drawers have results. Uses the input's own id as prefix.
   */
  function scopeIds(container, prefix) {
    if (!prefix) return;

    // Rewrite id attributes
    container.querySelectorAll('[id]').forEach(function (el) {
      el.id = prefix + '-' + el.id;
    });

    // Rewrite aria-controls references to match
    container.querySelectorAll('[aria-controls]').forEach(function (el) {
      el.setAttribute('aria-controls', prefix + '-' + el.getAttribute('aria-controls'));
    });

    // Rewrite for attributes (labels)
    container.querySelectorAll('[for]').forEach(function (el) {
      el.setAttribute('for', prefix + '-' + el.getAttribute('for'));
    });
  }

  /**
   * Clear the drawer and optionally announce a message.
   * `resultsContainer` is guarded like `footer` and `statusEl`: this runs inside
   * promise handlers, where a null dereference becomes a swallowed rejection
   * rather than a visible error.
   */
  function clearDrawer(resultsContainer, footer, statusEl, announce) {
    if (resultsContainer) resultsContainer.innerHTML = '&nbsp;';
    if (footer) footer.hidden = true;
    if (statusEl) statusEl.textContent = announce || '';
  }

  function showSearchError(resultsContainer, footer, statusEl) {
    clearDrawer(resultsContainer, footer, statusEl, statusEl && statusEl.getAttribute('data-error-text'));
  }

  function onSearch(input) {
    var state = getState(input);
    var term = input.value.trim();
    var panel = input.closest('.sexyhair-search-drawer__panel');
    if (!panel) return;

    var resultsContainer = panel.querySelector('[data-predictive-search]');
    var footer = panel.querySelector('[data-search-footer]');
    var footerLink = panel.querySelector('[data-search-footer-link]');
    var statusEl = panel.querySelector('[data-search-status]');

    // Cancel any in-flight request for this input
    if (state.abortController) {
      state.abortController.abort();
    }

    if (term === '') {
      clearDrawer(resultsContainer, footer, statusEl);
      return;
    }

    state.abortController = new AbortController();

    // Announce loading state. The strings live on the element as data
    // attributes so they stay translatable — never hardcode them here.
    if (statusEl) statusEl.textContent = statusEl.getAttribute('data-loading-text') || '';

    fetch(
      window.routes.predictive_search_url +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,query,collection' +
        '&resources[limit]=10&resources[limit_scope]=each' +
        '&section_id=sexyhair-predictive-search',
      { signal: state.abortController.signal }
    )
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        // Parse inertly. Assigning to a live element's innerHTML starts image and
        // iframe loads and fires inline onerror/onload during the parse; a
        // DOMParser document does neither. Matches upstream Dawn's approach.
        var parsed = new DOMParser().parseFromString(text, 'text/html');
        var section = parsed.querySelector('#shopify-section-sexyhair-predictive-search');
        if (section) {
          // Scope IDs to prevent duplicates between mobile + desktop drawers
          scopeIds(section, input.id);

          // Extract live-region count text before injecting
          var countEl = section.querySelector('[data-predictive-search-live-region-count-value]');
          var countText = countEl ? countEl.textContent.trim() : '';

          resultsContainer.innerHTML = section.innerHTML;

          // Update screen reader live region
          if (statusEl) statusEl.textContent = countText;

          // Show the pinned footer and update its link using the Liquid-provided base URL
          if (footer && footerLink) {
            var baseUrl = footerLink.getAttribute('data-base-url') || '/search';
            footerLink.href = baseUrl + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
            footer.hidden = false;
          }
        } else {
          // The fetch asked for section_id=sexyhair-predictive-search and parses
          // #shopify-section-sexyhair-predictive-search out of the response. A
          // mismatch between those two still returns HTTP 200, so .catch never
          // fires — without this branch the drawer would silently stay empty.
          showSearchError(resultsContainer, footer, statusEl);
          console.error(
            'Predictive search: no #shopify-section-sexyhair-predictive-search in the response. ' +
              'The section_id requested and the wrapper id parsed must match.'
          );
        }
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return;
        showSearchError(resultsContainer, footer, statusEl);
        console.error('Predictive search error:', error);
      });
  }

  // Document-level event delegation — survives section reloads
  document.addEventListener('input', function (e) {
    if (!e.target.matches('.sexyhair-search-drawer__input')) return;

    var input = e.target;
    var state = getState(input);

    clearTimeout(state.debounceTimer);
    state.debounceTimer = setTimeout(function () {
      onSearch(input);
    }, 200);
  });
})();
